import React, { useMemo } from 'react';
import { pxToRem } from '@withjoy/joykit/theme';
import { Flex, useDisclosure } from '@withjoy/joykit';
import { ExpediaIcon, ExpediaSmallIcon, BookingIcon, BookingSmallIcon } from '@assets/icons';
import { useRemoteFeatureFlag } from '@shared/core/featureFlags/remoteFeatureFlags';
import { AccommodationCombined } from '../../../Accommodations/Accommodation.types';
import { EventVenue } from '@shared/utils/eventPlace';
import { ComparisonDialog } from './components/ComparisonDialog';
import { ProviderLink } from './components/ProviderLink';
import { ProviderIcons } from './components/ProviderIcons';
import { useEventInfo } from '@shared/utils/eventInfo';
import { Rate } from '@shared/hooks/useTravelPlacesPriceComparison/useTravelPlacesPriceComparison';

export enum Provider {
  EXPEDIA = 'expedia.com',
  BOOKING = 'booking.com',
}

export const telemetryDestination = { [Provider.EXPEDIA]: 'ExpediaPriceCompare', [Provider.BOOKING]: 'BookingPriceCompare' };

type IconMap = Record<
  string,
  {
    small: JSX.Element;
    large: JSX.Element;
  }
>;

export const ComparisonIcon: IconMap = {
  [Provider.EXPEDIA]: { small: <ExpediaSmallIcon />, large: <ExpediaIcon /> },
  [Provider.BOOKING]: { small: <BookingSmallIcon />, large: <BookingIcon /> },
};

type CompareToPriceProps = {
  accommodation: AccommodationCombined;
  locationInfo?: EventVenue;
  rates: Rate[];
};

export const CompareToPrice: React.FC<CompareToPriceProps> = ({ accommodation, locationInfo, rates }) => {
  const { eventHandle } = useEventInfo();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { value, loading } = useRemoteFeatureFlag('accommodationsPriceComparisonExperiment');

  // always show the highest
  const priceToShow = useMemo(() => [...rates].sort((a, b) => b.priceInMinorUnits - a.priceInMinorUnits)[0]?.price ?? '', [rates]);

  if (!rates.length || loading || !value || value === 'no-comparison' || !priceToShow) {
    return null;
  }

  return (
    <>
      <Flex
        flexDirection="column"
        width="100%"
      >
        <Flex
          flexDirection="column"
          alignItems="center"
          justifyContent="space-between"
        >
          <Flex
            width="100%"
            typographyVariant="body3"
            height={pxToRem(48)}
            alignItems="center"
            color="inputPlaceholderDisabled"
            borderTop="solid 1px"
            borderColor="mono5"
            justifyContent="space-between"
          >
            <ProviderIcons rates={rates} />
            <ProviderLink
              accommodation={accommodation}
              priceToShow={priceToShow}
              rates={rates}
              onOpenDialog={onOpen}
            />
          </Flex>
        </Flex>
      </Flex>
      <ComparisonDialog
        eventHandle={eventHandle ?? ''}
        isOpen={isOpen}
        onClose={onClose}
        accommodation={accommodation}
        locationInfo={locationInfo}
        rates={rates}
      />
    </>
  );
};
